import React, { Dispatch, SetStateAction, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CalenderIcon from "@/assets/icons/CalenderGray.svg";
import MailIcon from "@/assets/icons/settingsModal/MailIconBg.svg";
import FileUploadField from "@/components/ui/FileUploadField";
import { FileDetails } from "@/types/types";
import { CustomButton } from "@/components/ui/Buttons";
import { useUploadReshippingDetailsMutation } from "@/redux/feature/productShipping/productShipping";
import { TCreatorShippingDetails } from "@/redux/feature/productShipping/productShippingType";
import toast from "react-hot-toast";

interface ShippingConfirmationModalProps {
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  creator_shipping_details: TCreatorShippingDetails;
  id: string;
}

const ShippingConfirmationModal = ({
  setIsModalOpen,
  creator_shipping_details,
  id,
}: ShippingConfirmationModalProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [deliveryFile, setDeliveryFile] = useState<FileDetails | null>(null);
  const [loading, setIsLoading] = useState(false);

  const [uploadReshippingDetails] = useUploadReshippingDetailsMutation();

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = async () => {
    if (!selectedDate || !deliveryFile?.live_url) {
      toast.error("Please select a date and upload the shipping invoice.");
      return;
    }

    setIsLoading(true);

    try {
      const formattedDate = selectedDate.toISOString();
      const response = await uploadReshippingDetails({
        id,
        shipping_invoice: deliveryFile,
        expected_delivery_date: formattedDate,
      }).unwrap();

      console.log("API Response:", response);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to upload reshipping details:", error);
      toast.error("Failed to upload reshipping details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const isSubmitDisabled = () => {
    return !selectedDate || !deliveryFile?.live_url;
  };

  return (
    <div className="space-y-5">
      <MailIcon />
      <h6 className="text-lg font-semibold leading-[26px] tracking-[-1.5%]">
        Shipping Confirmation
      </h6>
      <ShippingDetails creator_shipping_details={creator_shipping_details} />
      <ExpectedDeliveryDateField
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        deliveryFile={deliveryFile}
        setDeliveryFile={setDeliveryFile}
      />
      <ActionButtons
        loading={loading}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        isSubmitDisabled={isSubmitDisabled}
      />
    </div>
  );
};

// ShippingDetails Component
interface ShippingDetailsProps {
  creator_shipping_details: TCreatorShippingDetails;
}

const ShippingDetails: React.FC<ShippingDetailsProps> = ({
  creator_shipping_details,
}) => (
  <div className="border border-[#E9EBEE] py-2 px-2.5 rounded-[4px] space-y-2">
    <h6 className="text-[#667085] text-sm font-semibold leading-[26px] tracking-[-1.5%] underline underline-offset-4">
      Shipping Details
    </h6>
    <div className="flex gap-x-2 justify-between">
      <DetailItem label="Creator Name" value={creator_shipping_details?.name} />
      <DetailItem label="Creator City" value={creator_shipping_details?.city} />
      <DetailItem label="Creator Pincode" value="11000022" />{" "}
      {/* Add pincode if available */}
    </div>
    <DetailItem
      label="Creator Address"
      value={creator_shipping_details?.address}
    />
    <DetailItem
      label="Creator Contact Number"
      value={creator_shipping_details?.number}
    />
  </div>
);

// DetailItem Component
interface DetailItemProps {
  label: string;
  value: string;
}

const DetailItem: React.FC<DetailItemProps> = ({ label, value }) => (
  <div className="space-y-1">
    <label className="text-[#98A2B3] text-[10px] leading-4 tracking-[-1%]">
      {label}
    </label>
    <p className="text-[#667085] text-xs leading-[18px] tracking-[-1%] font-medium">
      {value}
    </p>
  </div>
);

// ExpectedDeliveryDateField Component
interface ExpectedDeliveryDateFieldProps {
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  deliveryFile: FileDetails | null;
  setDeliveryFile: (file: FileDetails | null) => void;
}

const ExpectedDeliveryDateField: React.FC<ExpectedDeliveryDateFieldProps> = ({
  selectedDate,
  setSelectedDate,
  deliveryFile,
  setDeliveryFile,
}) => (
  <div className="space-y-1">
    <div className="px-[12px] flex items-center w-full border border-[#D0D5DD] rounded-[10px]">
      <p className="flex-1 text-[#363636] py-[10px] text-sm font-medium pr-[12px]">
        Expected Date of Delivery
      </p>
      <button className="flex-1 text-[#959FB2] leading-[15px] flex items-center py-[6px] border-[#D0D5DD] px-[12px] text-sm border-l">
        <DatePicker
          selected={selectedDate}
          onChange={(date: Date | null) => setSelectedDate(date)}
          dateFormat="dd/MM/yyyy"
          minDate={new Date()}
          placeholderText="Select a Date"
          className="w-[125px] text-sm font-medium text-gray-700 cursor-pointer pl-4 focus:outline-none"
          popperPlacement="bottom-start"
        />
        <CalenderIcon />
      </button>
    </div>
    <div className="mt-5">
      <p className="text-[#344054] text-sm font-medium leading-5 mb-2 mt-4">
        Upload the shipping invoice
      </p>
      <div className="border border-dashed border-[#B6BAC3] rounded-lg">
        <FileUploadField
          height="145px"
          onFilesUploaded={(files) => setDeliveryFile(files[0])}
          maxFiles={1}
        />
      </div>
    </div>
  </div>
);

// ActionButtons Component
interface ActionButtonsProps {
  loading: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitDisabled: () => boolean;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  loading,
  onCancel,
  onSubmit,
  isSubmitDisabled,
}) => (
  <div className="flex gap-x-3 mt-6">
    <CustomButton
      variant="outline"
      color="#344054"
      fontSize="16px"
      fontWeight="600"
      border="1px solid #D0D5DD"
      borderRadius="8px"
      padding="10px"
      className="flex-1"
      onClick={onCancel}
    >
      Cancel
    </CustomButton>
    <CustomButton
      backgroundColor="#17457D"
      fontSize="16px"
      fontWeight="600"
      border="1px solid #17457D"
      borderRadius="8px"
      padding="10px"
      className="flex-1"
      onClick={onSubmit}
      disabled={isSubmitDisabled()}
    >
      {loading ? "Confirming.." : "Confirm"}
    </CustomButton>
  </div>
);

export default ShippingConfirmationModal;
